import { useSyncExternalStore } from 'react';
import { onResume, reloadBlocked } from './resume';

/*
 * Installing DijitalApp as an app (PWA). Chromium browsers fire `beforeinstallprompt` once, early,
 * so it is captured at startup (startInstallWatch in main.tsx) and replayed when the user taps
 * "Install". iOS Safari has no prompt: there the user is shown the Share → Add to Home Screen steps.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallState = 'installed' | 'available' | 'ios' | 'unsupported';

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function standalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
function iosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/** Registers the service worker (offline shell) and starts listening for the install prompt. */
export function startInstallWatch(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    notify();
  });
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch(() => undefined);
  }
  if (import.meta.env.PROD) onResume((away) => void checkForUpdate(away));
}

/** The build's entry script, e.g. /assets/index-3f9a1c.js; it changes with every deploy. */
function entryOf(html: string): string | null {
  return /<script[^>]+type="module"[^>]+src="([^"]+)"/.exec(html)?.[1] ?? null;
}
let lastCheck = 0;

/**
 * An installed app stays open for days, so a deploy would otherwise wait until someone closes it. Coming
 * back after a while, the app asks whether a newer version is out; if so (and no call is in progress) it
 * reloads straight away, before the person starts doing anything. Drafts and the outbox survive a reload.
 */
async function checkForUpdate(away: number): Promise<void> {
  if (away < 60_000 || Date.now() - lastCheck < 60_000) return;
  lastCheck = Date.now();
  void navigator.serviceWorker?.getRegistration('/').then((r) => r?.update()).catch(() => undefined);
  const mine = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src');
  if (!mine) return;
  try {
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
    const latest = res.ok ? entryOf(await res.text()) : null;
    if (!latest || latest === mine) return;
    // Brought back by a tapped notification: its message (sent to this window a moment after it came to the front)
    // gets the time to arrive, and then the reload waits (resume.ts quietReloads) for the next time back.
    await new Promise((r) => setTimeout(r, 1000));
    if (!reloadBlocked()) location.reload();
    else lastCheck = 0;
  } catch {
    /* offline: next time */
  }
}

function snapshot(): InstallState {
  if (installed || standalone()) return 'installed';
  if (deferred) return 'available';
  if (iosSafari()) return 'ios';
  return 'unsupported';
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    snapshot,
  );
}

/** Shows the browser's install dialog; must run from a user gesture. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === 'accepted') installed = true;
  notify();
  return outcome === 'accepted';
}
